import React, { useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  getAllPostsAdmin,
  deletePost,
  publishPost,
  updatePost,
  getBlogImageUrl,
  CATEGORIA_LABELS,
  formatPostDate,
} from '../../services/blogService';
import { ConfigAgenteModalV2, KeywordsModalV2, AgentDispatchModalV2 } from './BlogModalsV2';

// ============ Themes (sync com AdminPageV2) ============
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7', surface: '#FFFFFF', surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B', inkSoft: '#3D434F', muted: '#6B7280',
  line: '#E8E5DE', gold: '#B8935A', goldSoft: '#F2EBDD',
  ok: '#2F7D5B', warn: '#B8935A', danger: '#9A3B3B',
  shadow: 'rgba(0,0,0,.06)',
};
const darkTheme = {
  mode: 'dark',
  bg: '#0F1117', surface: '#181B23', surfaceAlt: '#1F2330',
  ink: '#F1ECE2', inkSoft: '#C9C4B8', muted: '#8A8F9A',
  line: '#262A36', gold: '#C9A06A', goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089', warn: '#C9A06A', danger: '#C5544A',
  shadow: 'rgba(0,0,0,.4)',
};
const THEME_KEY = 'mf_admin_v2_theme';

// ============ Styled ============
const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, sans-serif;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Sidebar = styled.aside`
  background: ${p => p.theme.surface};
  border-right: 1px solid ${p => p.theme.line};
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: sticky;
  top: 0;
  align-self: start;
  height: 100vh;
  overflow-y: auto;
  @media (max-width: 900px) {
    position: static;
    height: auto;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 14px 20px;
    gap: 6px;
    align-items: center;
    border-right: none;
    border-bottom: 1px solid ${p => p.theme.line};
  }
`;
const Brand = styled.div`
  padding: 0 8px 24px;
  border-bottom: 1px solid ${p => p.theme.line};
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  img { height: 56px; width: auto; max-width: 180px; object-fit: contain; display: block; }
  span.tagline {
    font-family: 'Inter', sans-serif;
    font-size: 10px; letter-spacing: 3px;
    color: ${p => p.theme.gold};
    text-transform: uppercase; font-weight: 500;
  }
  @media (max-width: 900px) {
    border-bottom: none; padding: 0; margin: 0 auto 0 0;
    flex-direction: row; gap: 12px;
    img { height: 36px; }
    span.tagline { display: none; }
  }
`;
const NavGroup = styled.div`
  font-size: 10px; letter-spacing: 2.5px;
  color: ${p => p.theme.muted}; text-transform: uppercase;
  padding: 16px 8px 8px; font-weight: 500;
  @media (max-width: 900px) { display: none; }
`;
const NavItem = styled.button`
  display: flex; align-items: center; gap: 12px;
  width: 100%; border: none; text-align: left;
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 11px 14px; border-radius: 8px;
  font-size: 14px; font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer; letter-spacing: 0.2px;
  font-family: inherit;
  &:hover { background: ${p => p.$active ? p.theme.goldSoft : p.theme.surfaceAlt}; }
  &:disabled { color: ${p => p.theme.muted}; cursor: not-allowed; opacity: .6; }
  small {
    margin-left: auto;
    font-size: 9px; letter-spacing: 1.5px;
    color: ${p => p.theme.gold}; text-transform: uppercase;
  }
`;
const Main = styled.main`
  display: flex; flex-direction: column; min-width: 0;
`;
const Topbar = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 40px; border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  flex-wrap: wrap; gap: 12px;
  @media (max-width: 600px) { padding: 14px 20px; }
`;
const Crumb = styled.div`
  font-size: 12px; letter-spacing: 1.5px;
  color: ${p => p.theme.muted}; text-transform: uppercase;
  span { color: ${p => p.theme.ink}; font-weight: 500; }
`;
const TopActions = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
`;
const TopBtn = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.$primary ? p.theme.ink : p.theme.surface};
  color: ${p => p.$primary ? p.theme.bg : p.theme.ink};
  padding: 9px 16px; border-radius: 6px;
  font-size: 13px; font-weight: 500; letter-spacing: 0.3px;
  cursor: pointer; font-family: inherit;
  transition: all .15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 2px 8px ${p => p.theme.shadow}; }
`;
const ThemeToggle = styled.button`
  border: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface};
  color: ${p => p.theme.ink};
  width: 38px; height: 38px; border-radius: 50%;
  cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: all .2s;
  &:hover { background: ${p => p.theme.surfaceAlt}; transform: rotate(15deg); }
`;
const Content = styled.div`
  padding: 40px 48px; width: 100%;
  @media (max-width: 1100px) { padding: 32px 28px; }
  @media (max-width: 600px) { padding: 24px 16px; }
`;

const PageHead = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  gap: 24px; flex-wrap: wrap; margin-bottom: 28px;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 36px; font-weight: 500;
    color: ${p => p.theme.ink}; margin: 0 0 4px;
  }
  p { color: ${p => p.theme.muted}; font-size: 13px; }
`;

const FilterBar = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap;
  margin-bottom: 22px;
`;
const FilterBtn = styled.button`
  border: 1px solid ${p => p.$active ? p.theme.gold : p.theme.line};
  background: ${p => p.$active ? p.theme.goldSoft : p.theme.surface};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 7px 14px; border-radius: 6px;
  font-size: 12px; letter-spacing: 0.5px; font-weight: 500;
  cursor: pointer; font-family: inherit;
  &:hover { border-color: ${p => p.theme.gold}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;
const Card = styled.article`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  overflow: hidden;
  display: flex; flex-direction: column;
  transition: transform .2s, box-shadow .2s;
  &:hover { transform: translateY(-3px); box-shadow: 0 8px 24px ${p => p.theme.shadow}; }
`;
const CardCover = styled.div`
  aspect-ratio: 16/9;
  background: ${p => p.theme.surfaceAlt};
  overflow: hidden;
  border-bottom: 1px solid ${p => p.theme.line};
  position: relative;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .ph {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    color: ${p => p.theme.muted};
    font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  }
`;
const CoverBadges = styled.div`
  position: absolute; top: 10px; left: 10px;
  display: flex; gap: 6px; flex-wrap: wrap;
`;
const Badge = styled.span`
  font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase;
  background: ${p => p.$gold ? p.theme.gold : 'rgba(0,0,0,.65)'};
  color: #fff; padding: 4px 8px; border-radius: 4px; font-weight: 500;
  backdrop-filter: blur(6px);
`;
const CardBody = styled.div`
  padding: 16px 18px 14px;
  display: flex; flex-direction: column; gap: 6px; flex: 1;
`;
const Cat = styled.div`
  font-size: 10px; letter-spacing: 2px; color: ${p => p.theme.gold};
  text-transform: uppercase; font-weight: 600;
`;
const CardTitle = styled.h3`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 19px; font-weight: 600;
  color: ${p => p.theme.ink};
  line-height: 1.25;
`;
const CardExcerpt = styled.p`
  font-size: 12px; color: ${p => p.theme.muted};
  margin: 0; line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const CardMeta = styled.div`
  display: flex; gap: 10px; font-size: 11px; color: ${p => p.theme.inkSoft};
  margin-top: 4px; padding-top: 8px; border-top: 1px solid ${p => p.theme.line};
`;
const CardActions = styled.div`
  display: flex;
  border-top: 1px solid ${p => p.theme.line};
  button {
    flex: 1; min-width: 0;
    border: none; background: transparent;
    color: ${p => p.theme.inkSoft};
    padding: 10px 6px;
    font-size: 12px; letter-spacing: 0.2px;
    cursor: pointer; font-family: inherit; font-weight: 500;
    border-right: 1px solid ${p => p.theme.line};
    transition: background .15s, color .15s;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &:last-child { border-right: none; }
    &:hover { background: ${p => p.theme.surfaceAlt}; color: ${p => p.theme.gold}; }
    &[data-danger="true"]:hover { color: #C5544A; }
  }
`;

const Empty = styled.div`
  padding: 60px 20px; text-align: center; color: ${p => p.theme.muted}; font-size: 13px;
`;

// ============ Helpers ============
const ACTIVE_RUNS_KEY = 'mf_blog_active_dispatches';

// ============ Active runs banner ============
const RunsBanner = styled.div`
  display: flex; flex-direction: column; gap: 10px;
  margin-bottom: 22px;
`;
const RunCard = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.gold};
  border-radius: 12px;
  padding: 18px 22px;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: ${p => p.theme.gold};
    opacity: .6;
  }
  .head {
    display: flex; align-items: center; gap: 14px;
    margin-bottom: 14px;
  }
  .spinner {
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 2.5px solid ${p => p.theme.gold};
    border-top-color: transparent;
    animation: mfblog-spin 1s linear infinite;
    flex-shrink: 0;
  }
  @keyframes mfblog-spin { to { transform: rotate(360deg); } }
  .info { flex: 1; min-width: 0; }
  .phase {
    font-size: 14px; font-weight: 600;
    color: ${p => p.theme.ink};
    margin-bottom: 4px;
    display: flex; align-items: center; gap: 8px;
  }
  .phase-emoji { font-size: 16px; }
  .tema {
    font-size: 12px; color: ${p => p.theme.muted};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    letter-spacing: 0.2px;
  }
  .close {
    background: transparent; border: none;
    color: ${p => p.theme.muted}; cursor: pointer; font-size: 20px;
    padding: 0 4px; line-height: 1;
    &:hover { color: ${p => p.theme.danger}; }
  }
  .bar {
    height: 6px;
    background: ${p => p.theme.surfaceAlt};
    border-radius: 999px;
    overflow: hidden;
    position: relative;
  }
  .fill {
    height: 100%;
    background: linear-gradient(90deg, ${p => p.theme.gold} 0%, ${p => p.theme.goldSoft} 100%);
    border-radius: 999px;
    transition: width .5s ease;
    position: relative;
    &::after {
      content: '';
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%);
      animation: mfblog-shimmer 1.6s linear infinite;
    }
  }
  @keyframes mfblog-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
  .meta {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 10px;
    font-size: 11px;
    letter-spacing: 1px;
    color: ${p => p.theme.muted};
    text-transform: uppercase;
    font-weight: 500;
  }
  .meta .pct {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 18px; font-weight: 600;
    color: ${p => p.theme.gold};
    letter-spacing: 0;
    text-transform: none;
  }
`;

// ============ Component ============
export default function BlogPageV2({
  onExit,
  onNew,
  onEdit,
  onLogout,
  onNavDashboard,
  onNavImoveis,
  onNavAmenidades,
  onNavCorretores,
  onNavVinculos,
  onNavConfig,
  goClassic,
}) {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  });
  const theme = mode === 'dark' ? darkTheme : lightTheme;
  const toggleMode = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem(THEME_KEY, next);
  };

  // ===== Modals =====
  const [modalConfig, setModalConfig] = useState(false);
  const [modalKeywords, setModalKeywords] = useState(false);
  const [modalAgent, setModalAgent] = useState(false);

  // ===== Active agent runs =====
  const [activeRuns, setActiveRuns] = useState(() => {
    try {
      const stored = sessionStorage.getItem(ACTIVE_RUNS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(ACTIVE_RUNS_KEY, JSON.stringify(activeRuns)); } catch {}
  }, [activeRuns]);

  const dismissRun = (runId) => setActiveRuns(prev => prev.filter(r => r.runId !== runId));

  // Tick a cada segundo pra atualizar timer/progresso dos runs ativos
  const [, setTick] = useState(0);
  useEffect(() => {
    if (activeRuns.length === 0) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeRuns.length]);

  const fetchPosts = async () => {
    setLoading(true);
    const data = await getAllPostsAdmin();
    setPosts(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, []);

  // Poll enquanto houver runs ativas
  useEffect(() => {
    if (activeRuns.length === 0) return;
    const check = async () => {
      const data = await getAllPostsAdmin();
      setPosts(data || []);
      const stillActive = [];
      for (const run of activeRuns) {
        const match = (data || []).find(p =>
          p.gerado_por === 'agente'
          && new Date(p.created_at).getTime() >= run.dispatchedAt - 5000
          && (p.bairro_relacionado === run.bairro_alvo || (p.titulo || '').toLowerCase().includes((run.tema || '').toLowerCase().slice(0, 20)))
        );
        if (match) continue; // run terminou, remove
        if (Date.now() - run.dispatchedAt > 15 * 60 * 1000) continue; // timeout 15min
        stillActive.push(run);
      }
      if (stillActive.length !== activeRuns.length) setActiveRuns(stillActive);
    };
    check();
    const interval = setInterval(check, 8000);
    return () => clearInterval(interval);
  }, [activeRuns]);

  const filtered = useMemo(() => {
    if (filter === 'all') return posts;
    return posts.filter(p => p.status === filter);
  }, [posts, filter]);

  const counts = useMemo(() => ({
    all: posts.length,
    publicado: posts.filter(p => p.status === 'publicado').length,
    rascunho: posts.filter(p => p.status === 'rascunho').length,
  }), [posts]);

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este artigo?')) return;
    await deletePost(id);
    fetchPosts();
  };

  const handlePublish = async (p) => {
    if (p.status === 'publicado') {
      if (!window.confirm('Despublicar este artigo?')) return;
      await updatePost(p.id, { status: 'rascunho' });
    } else {
      await publishPost(p.id);
    }
    fetchPosts();
  };

  return (
    <ThemeProvider theme={theme}>
      <Shell>
        <Sidebar>
          <Brand>
            <img
              src={mode === 'dark' ? '/mf-real-state/logos/mf-logo-dark.png' : '/mf-real-state/logos/mf-logo-light.png'}
              alt="MF Imóveis"
            />
            <span className="tagline">real estate</span>
          </Brand>

          <NavGroup>Painel</NavGroup>
          <NavItem onClick={onNavDashboard}>Visão geral</NavItem>
          <NavItem onClick={onNavImoveis}>Imóveis</NavItem>
          <NavItem onClick={onNavAmenidades} style={{ paddingLeft: 28, fontSize: 13 }}>↳ Amenidades</NavItem>
          <NavItem onClick={onNavCorretores}>Corretores</NavItem>
          <NavItem onClick={onNavVinculos} style={{ paddingLeft: 28, fontSize: 13 }}>↳ Vínculos</NavItem>
          <NavItem disabled>Leads <small>em breve</small></NavItem>
          <NavItem disabled>Visitas <small>em breve</small></NavItem>
          <NavItem $active>Blog</NavItem>

          <NavGroup>Marketing</NavGroup>
          <NavItem onClick={() => { localStorage.setItem('mf_admin_v2_nav', 'disparos'); onExit(); }}>Disparos</NavItem>

          <NavGroup>Sistema</NavGroup>
          <NavItem onClick={onNavConfig}>Configurações</NavItem>
          <NavItem onClick={onLogout}>Sair</NavItem>
        </Sidebar>

        <Main>
          <Topbar>
            <Crumb>Painel · <span>Blog</span></Crumb>
            <TopActions>
              <ThemeToggle onClick={toggleMode} title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
                {mode === 'dark' ? '☀' : '☾'}
              </ThemeToggle>
              <TopBtn onClick={() => setModalConfig(true)} title="Configurar o agente de IA">⚙ Config Agente</TopBtn>
              <TopBtn onClick={() => setModalKeywords(true)} title="Keywords estáticas">🔑 Keywords</TopBtn>
              <TopBtn onClick={() => setModalAgent(true)} title="Gerar artigo com agente">🤖 Gerar com agente</TopBtn>
              <TopBtn $primary onClick={onNew}>+ Novo artigo</TopBtn>
            </TopActions>
          </Topbar>

          <Content>
            {activeRuns.length > 0 && (
              <RunsBanner>
                {activeRuns.map(run => {
                  const EXPECTED_S = 310; // ~5min médio das runs reais
                  const elapsedS = Math.floor((Date.now() - run.dispatchedAt) / 1000);
                  const remainingS = Math.max(0, EXPECTED_S - elapsedS);
                  // Cap em 95% até realmente terminar (evita "100% travado")
                  const progress = Math.min(95, (elapsedS / EXPECTED_S) * 100);
                  const pct = (elapsedS / EXPECTED_S) * 100;

                  const phase =
                    pct < 25 ? { emoji: '🔍', text: 'Pesquisando na web e lendo o inventário' } :
                    pct < 35 ? { emoji: '📐', text: 'Estruturando o artigo (definindo ângulo único)' } :
                    pct < 75 ? { emoji: '✍️', text: 'Escrevendo o conteúdo (1200-1800 palavras)' } :
                    pct < 85 ? { emoji: '🎯', text: 'Avaliando qualidade (critérios E-E-A-T)' } :
                    pct < 100 ? { emoji: '📦', text: 'Gerando SEO meta e salvando' } :
                    { emoji: '⏳', text: 'Finalizando (passou do esperado, mas continua rodando)' };

                  const fmtTime = (s) => {
                    if (s < 60) return `${s}s`;
                    const m = Math.floor(s / 60);
                    const sec = s % 60;
                    return `${m}m ${sec.toString().padStart(2, '0')}s`;
                  };

                  return (
                    <RunCard key={run.runId}>
                      <div className="head">
                        <span className="spinner" />
                        <div className="info">
                          <div className="phase">
                            <span className="phase-emoji">{phase.emoji}</span>
                            {phase.text}…
                          </div>
                          <div className="tema">
                            {run.tema}{run.bairro_alvo && ` · ${run.bairro_alvo}`}
                          </div>
                        </div>
                        <button className="close" onClick={() => dismissRun(run.runId)} title="Ocultar (continua rodando no servidor)">×</button>
                      </div>
                      <div className="bar">
                        <div className="fill" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="meta">
                        <span>⏱ {fmtTime(elapsedS)} decorridos</span>
                        <span className="pct">{Math.round(progress)}%</span>
                        <span>{remainingS > 0 ? `≈ ${fmtTime(remainingS)} restantes` : 'concluindo…'}</span>
                      </div>
                    </RunCard>
                  );
                })}
              </RunsBanner>
            )}

            <PageHead>
              <div>
                <h1>Blog</h1>
                <p>{filtered.length} {filtered.length === 1 ? 'artigo' : 'artigos'}{filtered.length !== posts.length && ` de ${posts.length}`}</p>
              </div>
            </PageHead>

            <FilterBar>
              <FilterBtn $active={filter === 'all'} onClick={() => setFilter('all')}>Todos · {counts.all}</FilterBtn>
              <FilterBtn $active={filter === 'publicado'} onClick={() => setFilter('publicado')}>Publicados · {counts.publicado}</FilterBtn>
              <FilterBtn $active={filter === 'rascunho'} onClick={() => setFilter('rascunho')}>Rascunhos · {counts.rascunho}</FilterBtn>
            </FilterBar>

            {loading ? (
              <Empty>Carregando…</Empty>
            ) : filtered.length === 0 ? (
              <Empty>Nenhum artigo {filter !== 'all' ? `(${filter})` : ''} encontrado.</Empty>
            ) : (
              <Grid>
                {filtered.map(p => {
                  const cover = p.imagem_capa
                    ? (p.imagem_capa.startsWith('http') ? p.imagem_capa : getBlogImageUrl(p.imagem_capa))
                    : null;
                  return (
                    <Card key={p.id}>
                      <CardCover>
                        {cover ? <img src={cover} alt="" loading="lazy" /> : <span className="ph">sem capa</span>}
                        <CoverBadges>
                          {p.status === 'publicado' && <Badge>Publicado</Badge>}
                          {p.status === 'rascunho' && <Badge>Rascunho</Badge>}
                          {p.gerado_por === 'agente' && <Badge $gold>🤖 IA</Badge>}
                        </CoverBadges>
                      </CardCover>
                      <CardBody>
                        <Cat>{CATEGORIA_LABELS[p.categoria] || p.categoria || '—'}</Cat>
                        <CardTitle>{p.titulo || '(Sem título)'}</CardTitle>
                        {p.resumo && <CardExcerpt>{p.resumo}</CardExcerpt>}
                        <CardMeta>
                          {p.published_at && <span>{formatPostDate(p.published_at)}</span>}
                          {p.qualidade_score != null && <span>· Score {p.qualidade_score}</span>}
                          {p.bairro_relacionado && <span>· {p.bairro_relacionado}</span>}
                        </CardMeta>
                      </CardBody>
                      <CardActions>
                        <button onClick={() => onEdit(p)}>Editar</button>
                        <button onClick={() => handlePublish(p)}>
                          {p.status === 'publicado' ? 'Despublicar' : 'Publicar'}
                        </button>
                        <button data-danger="true" onClick={() => handleDelete(p.id)}>Excluir</button>
                      </CardActions>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </Content>
        </Main>

        <ConfigAgenteModalV2
          open={modalConfig}
          onClose={() => setModalConfig(false)}
        />
        <KeywordsModalV2
          open={modalKeywords}
          onClose={() => setModalKeywords(false)}
        />
        <AgentDispatchModalV2
          open={modalAgent}
          onClose={() => setModalAgent(false)}
          onDispatched={(run) => setActiveRuns(prev => [...prev, run])}
        />
      </Shell>
    </ThemeProvider>
  );
}
